package fyi.moko.hotpl.data.network

import fyi.moko.hotpl.domain.model.GeoPoint
import fyi.moko.hotpl.domain.model.PlaceCategory
import fyi.moko.hotpl.domain.model.haversineMeters
import fyi.moko.hotpl.domain.model.GeohashEncoder
import fyi.moko.hotpl.domain.model.SearchPrediction
import fyi.moko.hotpl.domain.model.YellowPageEntry
import fyi.moko.hotpl.domain.service.PlaceSearchException
import fyi.moko.hotpl.domain.service.PlaceSearchProvider
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.isSuccess
import kotlinx.coroutines.delay
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.math.ceil
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.DurationUnit
import kotlin.time.TimeSource

/**
 * Free-tier [PlaceSearchProvider] backed by OpenStreetMap's Nominatim
 * (nominatim.openstreetmap.org) — no API key, no per-request cost. Intended for
 * free/anonymous users; paying users still get [KtorPlaceSearchProvider] (Google Places)
 * for its richer, more reliable business data (see docs/HOTPL_MASTER.md free-tier note).
 *
 * OSM coverage rarely includes phone/website for small businesses, so
 * [YellowPageEntry.phoneNumber]/[YellowPageEntry.website] frequently come back null —
 * both are already nullable on [YellowPageEntry], so no schema change was needed.
 *
 * The coverage gap goes beyond missing fields, though: some real, currently-operating
 * businesses aren't in OSM's database *at all*. Verified 2026-07-21 against the live
 * public instance — "rök Coffee and Tea" (with or without the macron, with/without
 * "and"/"&") returns zero results, vs. e.g. "starbucks" (a large chain) returning
 * several correctly-located hits with phone/website populated from OSM's own
 * `extratags`. So this isn't just sparser data on a known place — smaller/newer/
 * independent businesses can be entirely absent, and `searchAutocomplete` correctly
 * returns an empty list in that case (no crash, no false match) — the Directory tab
 * shows "No directory results nearby" as expected. This is the real, user-facing
 * trade-off of the free tier vs. [KtorPlaceSearchProvider]'s Google-maintained data.
 *
 * IDEA (2026-07-21) → decided Hold (2026-07-29): a zero-result fallback via on-device
 * FunctionGemma tool-call + web search + re-geocode was considered and evaluated against
 * real on-device measurements from a sandbox project, then against this project's own
 * prior AI-adoption strategy. Not building it now — see
 * docs/directory-zero-result-fallback-decision.md for the full comparison and re-open
 * triggers. [fyi.moko.hotpl.domain.service.FallbackPlaceSearchProvider] already exists as
 * the non-LLM primitive that decision doc's Option C would use instead.
 *
 * BIGGER IDEA (not designed, 2026-07-21): run FunctionGemma as a background job (not
 * inline with search) that extracts keywords from (a) queries that returned zero local
 * results and (b) places already saved to the journal, accumulating a per-user taste/
 * usage profile over time to personalize future search ranking. Distinct in kind from
 * the zero-result-fallback idea above — this is a recommendation feature, not a search
 * gap fix. Open questions before this goes anywhere: where the profile lives (local-only
 * strongly preferred — it's more sensitive than the existing PRIVATE/FRIENDS/PUBLIC
 * [fyi.moko.hotpl.domain.model.Visibility] model covers), and cold-start (useless until
 * enough queries/saves accumulate). Background-job shape should follow the existing
 * `SyncCoordinator` app-lifecycle-scope pattern rather than introducing WorkManager
 * (Android-only, doesn't fit iOS/JS).
 *
 * Nominatim's usage policy (https://operations.osmfoundation.org/policies/nominatim/)
 * caps the public instance at 1 request/second and requires a descriptive User-Agent;
 * both are enforced here via [minRequestIntervalMs] and [userAgent]. A `placeId` here is
 * Nominatim's own OSM id format (`"{N|W|R}{osmId}"`), reused directly as the `osm_ids`
 * parameter for `/lookup` — no separate id-mapping table needed.
 */
class NominatimPlaceSearchProvider(
    private val client: HttpClient,
    private val userAgent: String = "Hotpla/1.0 (+https://github.com/kakasolg/hotpla; free-tier place search)",
    private val minRequestIntervalMs: Long = 1_000L,
) : PlaceSearchProvider {

    // Required by Nominatim's ODbL data license wherever results are displayed.
    override val attributionText: String = "Map data © OpenStreetMap contributors"

    private val throttleLock = Mutex()
    private var lastRequestMark: TimeSource.Monotonic.ValueTimeMark? = null

    override suspend fun searchAutocomplete(
        query: String,
        origin: GeoPoint?,
    ): List<SearchPrediction> {
        if (query.isBlank()) return emptyList()

        throttle()

        val viewbox = origin?.let(::viewboxAround)

        val httpResponse = client.get("https://nominatim.openstreetmap.org/search") {
            header("User-Agent", userAgent)
            url {
                parameters.append("q", stripConnectorWords(query.trim()))
                parameters.append("format", "jsonv2")
                parameters.append("accept-language", SEARCH_LANGUAGE_CODE)
                parameters.append("extratags", "1")
                parameters.append("limit", "10")
                if (viewbox != null) {
                    // Soft bias only (bounded=0) — a region hint, not a hard filter, matching
                    // the Google provider's circle bias which also only nudges ranking.
                    parameters.append("viewbox", viewbox)
                    parameters.append("bounded", "0")
                }
            }
        }
        val results: List<NominatimResult> = httpResponse.requireOk("search").body()

        return results.map { it.toSearchPrediction(origin) }
    }

    /**
     * Nominatim has no distance ranking and no query-less "what's nearby" endpoint, so this
     * does what it can: a bounded viewbox search re-sorted locally by the coordinates it
     * returns. A blank query yields nothing rather than something arbitrary — the caller
     * treats an empty list as "this provider can't answer that", not as "no places exist".
     */
    /**
     * 쿼리 없는 탐색은 못 한다 — Nominatim에 그런 엔드포인트가 없다. 화면은 이 값을 보고
     * "Near me"와 카테고리 칩을 감춘다. 감추지 않으면 눌러도 늘 빈 결과가 오고, 그걸 화면이
     * "주변에 없다"로 잘못 말하게 된다.
     */
    override val supportsNearbyBrowse: Boolean = false

    override suspend fun searchNearest(
        query: String,
        origin: GeoPoint,
        category: PlaceCategory?,
    ): List<SearchPrediction> {
        val trimmed = query.trim()
        if (trimmed.isBlank()) return emptyList()

        throttle()

        val httpResponse = client.get("https://nominatim.openstreetmap.org/search") {
            header("User-Agent", userAgent)
            url {
                parameters.append("q", stripConnectorWords(trimmed))
                parameters.append("format", "jsonv2")
                parameters.append("accept-language", SEARCH_LANGUAGE_CODE)
                parameters.append("extratags", "1")
                parameters.append("limit", "20")
                parameters.append("viewbox", viewboxAround(origin))
                // bounded=1 (unlike the autocomplete path's soft hint): the caller asked for
                // what is near, so results outside the box are wrong answers, not weak ones.
                parameters.append("bounded", "1")
            }
        }
        val results: List<NominatimResult> = httpResponse.requireOk("nearest search").body()

        return results
            .map { it.toSearchPrediction(origin) }
            .sortedWith(compareBy(nullsLast()) { it.distanceMeters })
    }

    override suspend fun fetchPlaceDetails(placeId: String): YellowPageEntry {
        throttle()

        val httpResponse = client.get("https://nominatim.openstreetmap.org/lookup") {
            header("User-Agent", userAgent)
            url {
                parameters.append("osm_ids", placeId)
                parameters.append("format", "jsonv2")
                parameters.append("accept-language", SEARCH_LANGUAGE_CODE)
                parameters.append("extratags", "1")
            }
        }
        val results: List<NominatimResult> = httpResponse.requireOk("lookup").body()
        val result = results.firstOrNull()
            ?: throw PlaceSearchException("Nominatim lookup found no match for place id '$placeId'")

        val lat = result.lat.toDouble()
        val lon = result.lon.toDouble()
        val name = result.name ?: result.display_name.substringBefore(",").trim()
        val normalizedName = YellowPageEntry.normalizeName(name)
        val geohash = GeohashEncoder.encode(lat, lon, YellowPageEntry.GEOHASH_PRECISION)
        val regionKey = geohash.take(YellowPageEntry.REGION_KEY_LENGTH)

        return YellowPageEntry(
            entryId = YellowPageEntry.canonicalId(geohash, normalizedName),
            canonicalId = placeId,
            name = name,
            normalizedName = normalizedName,
            geohash = geohash,
            regionKey = regionKey,
            latitude = lat,
            longitude = lon,
            tags = osmTypeTags(result.category, result.type),
            phoneNumber = result.extratags?.phone,
            website = result.extratags?.website,
        )
    }

    // Nominatim has no "distance from" parameter, but it does return coordinates — so the
    // distance is computed here rather than left null, keeping both providers' predictions
    // sortable by the same rule.
    private fun NominatimResult.toSearchPrediction(origin: GeoPoint?): SearchPrediction {
        val prefix = when (osm_type) {
            "node" -> "N"
            "way" -> "W"
            "relation" -> "R"
            else -> "N"
        }
        val primary = name ?: display_name.substringBefore(",").trim()
        val secondary = if (name != null) {
            display_name.removePrefix(name).removePrefix(",").trim()
        } else {
            display_name.substringAfter(",", "").trim()
        }
        val distance = origin?.let { from ->
            val latitude = lat.toDoubleOrNull()
            val longitude = lon.toDoubleOrNull()
            if (latitude == null || longitude == null) {
                null
            } else {
                haversineMeters(from, GeoPoint(latitude, longitude)).toInt()
            }
        }
        return SearchPrediction(
            placeId = "$prefix$osm_id",
            primaryText = primary,
            secondaryText = secondary,
            distanceMeters = distance,
        )
    }

    /**
     * ── 알려진 한계 ──────────────────────────────
     *  · Nominatim 자유 텍스트 검색은 "in"/"near"/"at"/"by"/"around" 같은 연결어가 섞이면
     *    같은 장소도 0건을 반환할 수 있다 (실측: "starbucks in Los Angeles" → 0건,
     *    "starbucks Los Angeles" → 정상 반환). VersionSandbox 프로젝트에서 검증된 우회.
     *  · `\b` 기반 정규식은 하이픈을 단어 경계로 취급해 "In-N-Out Burger" 같은 상호명을
     *    "-N-Out Burger"로 훼손하므로, `[\w-]` 부정 전후방탐색을 사용한다.
     *  · 연결어를 다 제거하면 빈 문자열이 되는 경우(예: 검색어가 "near" 단독)는 원본
     *    쿼리를 그대로 사용한다 — Nominatim에 빈 `q`를 보내는 것보다는 낫다.
     */
    private fun stripConnectorWords(query: String): String {
        val stripped = CONNECTOR_WORDS.replace(query, "").replace(WHITESPACE_RUN, " ").trim()
        return stripped.ifBlank { query }
    }

    /** A soft ~11km-wide bounding box around [center] as `"left,top,right,bottom"`. */
    /** Soft bias box around the searching user (not a geohash cell centre). */
    private fun viewboxAround(center: GeoPoint): String {
        val deltaDeg = 0.05
        val left = center.longitude - deltaDeg
        val right = center.longitude + deltaDeg
        val top = center.latitude + deltaDeg
        val bottom = center.latitude - deltaDeg
        return "$left,$top,$right,$bottom"
    }

    /** Blocks until at least [minRequestIntervalMs] has passed since the last call. */
    private suspend fun throttle() {
        throttleLock.withLock {
            val last = lastRequestMark
            if (last != null) {
                val remaining = minRequestIntervalMs.milliseconds - last.elapsedNow()
                if (remaining > Duration.ZERO) {
                    // Round up to whole milliseconds — delay(Duration) truncates, which could
                    // under-wait by up to 1ms and breach the "at least" rate-limit guarantee.
                    delay(ceil(remaining.toDouble(DurationUnit.MILLISECONDS)).toLong())
                }
            }
            lastRequestMark = TimeSource.Monotonic.markNow()
        }
    }

    private suspend fun HttpResponse.requireOk(operation: String): HttpResponse {
        if (!status.isSuccess()) {
            throw PlaceSearchException(
                "Nominatim $operation failed: HTTP ${status.value} ${bodyAsText()}",
            )
        }
        return this
    }

    private companion object {
        private val CONNECTOR_WORDS =
            Regex("""(?<![\w-])(in|near|at|by|around)(?![\w-])""", RegexOption.IGNORE_CASE)
        private val WHITESPACE_RUN = Regex("""\s+""")
    }
}
